import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createContext,
  useEffect,
  type PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { resolveRegistrationResumePath } from '../../features/registration/model/registration-navigation';
import {
  type EmployeeGoal,
  type EmployeeProfile,
  type EmployeeTipStats,
  type EmployeeWallet,
  getEmployeeGoal,
  getEmployeeProfile,
} from '../api/employee.service';
import { isInvalidAuthTokenError } from '../api/rpc-client';
import {
  changeAuthTokenPin,
  clearAuthToken,
  hasProtectedAuthToken,
  lockAuthToken,
  restoreAuthToken,
  saveAuthTokenPin,
  unlockAuthToken,
  verifyAuthTokenPin,
  writeAuthToken,
} from '../auth';
import { type CountryCode } from '../config';

const EMPLOYEE_QUERY_KEY = ['employee'] as const;
const EMPLOYEE_DASHBOARD_QUERY_KEY = ['employee', 'dashboard'] as const;
const EMPLOYEE_GOAL_QUERY_KEY = ['employee', 'goal'] as const;
const EMPLOYEE_PROFILE_QUERY_KEY = ['employee', 'profile'] as const;

type EmployeeSessionStatus = 'anonymous' | 'authenticated' | 'locked';
type EmployeeSessionSource = 'direct' | 'protected' | null;
type EmployeeSessionUnlockError = 'invalid_pin_or_session' | null;

export type EmployeeAuthContact = {
  countryCode: CountryCode;
  phoneDigits: string;
};

type EmployeeSessionContextValue = {
  authContact: EmployeeAuthContact | null;
  changeSessionPin: (currentPin: string, nextPin: string) => Promise<boolean>;
  clearSession: () => void;
  dismissUnlockError: () => void;
  handleInvalidSessionToken: () => void;
  hasPinProtection: boolean;
  saveSessionPin: (pin: string) => Promise<void>;
  setAuthContact: (contact: EmployeeAuthContact | null) => void;
  setSessionToken: (token: string) => void;
  status: EmployeeSessionStatus;
  unlockError: EmployeeSessionUnlockError;
  unlockSession: (pin: string) => Promise<void>;
  verifySessionPin: (pin: string) => Promise<boolean>;
};

type EmployeeSessionProviderProps = PropsWithChildren<{
  initialAuthContact?: EmployeeAuthContact | null;
  initialGoal?: EmployeeGoal | null;
  initialProfile?: EmployeeProfile | null;
  initialTipStats?: EmployeeTipStats | null;
  initialWallet?: EmployeeWallet | null;
}>;

const EmployeeSessionContext =
  createContext<EmployeeSessionContextValue | null>(null);

function createAppQueryClient() {
  return new QueryClient({
    defaultOptions: {
      mutations: {
        retry: false,
      },
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  });
}

function seedEmployeeSessionQueryCache(
  queryClient: QueryClient,
  {
    initialGoal,
    initialProfile,
    initialTipStats,
    initialWallet,
  }: EmployeeSessionProviderProps,
) {
  if (initialProfile) {
    queryClient.setQueryData(EMPLOYEE_PROFILE_QUERY_KEY, initialProfile);
  }

  if (initialGoal) {
    queryClient.setQueryData(EMPLOYEE_GOAL_QUERY_KEY, initialGoal);
  }

  if (initialProfile && initialTipStats && initialWallet) {
    queryClient.setQueryData(EMPLOYEE_DASHBOARD_QUERY_KEY, {
      profile: initialProfile,
      tipStats: initialTipStats,
      wallet: initialWallet,
    });
  }
}

function EmployeeSessionProviderInner({
  children,
  initialAuthContact = null,
}: PropsWithChildren<{
  initialAuthContact?: EmployeeAuthContact | null;
}>) {
  const queryClient = useQueryClient();
  const [sessionBootstrap] = useState(() => {
    const status = restoreAuthToken();

    return {
      hasPinProtection: hasProtectedAuthToken(),
      source: status === 'authenticated' ? 'direct' : null,
      status,
    } as const;
  });
  const [authContact, setAuthContactState] =
    useState<EmployeeAuthContact | null>(initialAuthContact);
  const [hasPinProtection, setHasPinProtection] = useState(
    sessionBootstrap.hasPinProtection,
  );
  const [status, setStatus] = useState<EmployeeSessionStatus>(
    sessionBootstrap.status,
  );
  const [unlockError, setUnlockError] =
    useState<EmployeeSessionUnlockError>(null);
  const sessionSourceRef = useRef<EmployeeSessionSource>(
    sessionBootstrap.source,
  );

  const clearSession = useCallback(() => {
    clearAuthToken();
    sessionSourceRef.current = null;
    setAuthContactState(null);
    setHasPinProtection(false);
    setStatus('anonymous');
    setUnlockError(null);
    queryClient.removeQueries({ queryKey: EMPLOYEE_QUERY_KEY });
  }, [queryClient]);

  const setAuthContact = useCallback((contact: EmployeeAuthContact | null) => {
    if (contact === null) {
      setAuthContactState(null);
      return;
    }

    setAuthContactState(contact);
  }, []);

  const setSessionToken = useCallback(
    (token: string) => {
      writeAuthToken(token);
      sessionSourceRef.current = 'direct';
      setStatus('authenticated');
      setUnlockError(null);
      queryClient.removeQueries({ queryKey: EMPLOYEE_QUERY_KEY });
    },
    [queryClient],
  );

  const saveSessionPin = useCallback(async (pin: string) => {
    await saveAuthTokenPin(pin);
    setHasPinProtection(true);
  }, []);

  const changeSessionPin = useCallback(
    async (currentPin: string, nextPin: string) => {
      const didChangePin = await changeAuthTokenPin(currentPin, nextPin);

      if (didChangePin) {
        setHasPinProtection(true);
      }

      return didChangePin;
    },
    [],
  );

  const verifySessionPin = useCallback(
    (pin: string) => verifyAuthTokenPin(pin),
    [],
  );

  const unlockSession = useCallback(
    async (pin: string) => {
      await unlockAuthToken(pin);
      sessionSourceRef.current = 'protected';
      setStatus('authenticated');
      setUnlockError(null);
      queryClient.removeQueries({ queryKey: EMPLOYEE_QUERY_KEY });
    },
    [queryClient],
  );

  const dismissUnlockError = useCallback(() => {
    setUnlockError(null);
  }, []);

  const handleInvalidSessionToken = useCallback(() => {
    queryClient.removeQueries({ queryKey: EMPLOYEE_QUERY_KEY });

    if (sessionSourceRef.current === 'protected' && hasProtectedAuthToken()) {
      lockAuthToken();
      sessionSourceRef.current = null;
      setHasPinProtection(true);
      setStatus('locked');
      setUnlockError('invalid_pin_or_session');
      return;
    }

    clearAuthToken();
    sessionSourceRef.current = null;
    setAuthContactState(null);
    setHasPinProtection(false);
    setStatus('anonymous');
    setUnlockError(null);
  }, [queryClient]);

  return (
    <EmployeeSessionContext.Provider
      value={{
        authContact,
        changeSessionPin,
        clearSession,
        dismissUnlockError,
        handleInvalidSessionToken,
        hasPinProtection,
        saveSessionPin,
        setAuthContact,
        setSessionToken,
        status,
        unlockError,
        unlockSession,
        verifySessionPin,
      }}
    >
      {children}
    </EmployeeSessionContext.Provider>
  );
}

export function EmployeeSessionProvider(props: EmployeeSessionProviderProps) {
  const [queryClient] = useState(() => {
    const nextQueryClient = createAppQueryClient();

    seedEmployeeSessionQueryCache(nextQueryClient, props);

    return nextQueryClient;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <EmployeeSessionProviderInner
        initialAuthContact={props.initialAuthContact}
      >
        {props.children}
      </EmployeeSessionProviderInner>
    </QueryClientProvider>
  );
}

export function useEmployeeSession(): EmployeeSessionContextValue {
  const context = useContext(EmployeeSessionContext);

  if (context === null) {
    throw new Error(
      'useEmployeeSession must be used within EmployeeSessionProvider',
    );
  }

  return context;
}

export function useResolvedAuthenticatedPath(): string | null {
  const { handleInvalidSessionToken, hasPinProtection, status } =
    useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: EMPLOYEE_PROFILE_QUERY_KEY,
  });
  const profile = profileQuery.data ?? null;
  const goalQuery = useQuery({
    enabled:
      status === 'authenticated' &&
      profile !== null &&
      profile.isOnboarded === false,
    queryFn: getEmployeeGoal,
    queryKey: EMPLOYEE_GOAL_QUERY_KEY,
  });
  const goal = goalQuery.data ?? null;
  const hasInvalidSessionTokenError =
    isInvalidAuthTokenError(profileQuery.error) ||
    isInvalidAuthTokenError(goalQuery.error);

  useEffect(() => {
    if (hasInvalidSessionTokenError) {
      handleInvalidSessionToken();
    }
  }, [handleInvalidSessionToken, hasInvalidSessionTokenError]);

  if (status === 'anonymous') {
    return '/login';
  }

  if (status === 'locked') {
    return '/unlock';
  }

  if (hasInvalidSessionTokenError) {
    return null;
  }

  if (!hasPinProtection) {
    return '/settings/pin';
  }

  if (profileQuery.isPending) {
    return null;
  }

  if (!profile) {
    return '/login';
  }

  if (profile.isOnboarded) {
    return '/home';
  }

  if (goalQuery.isPending) {
    return null;
  }

  return resolveRegistrationResumePath(profile, goal);
}
